import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { exportTableToPDF, exportGroupedTableToPDF } from '../utils/html2pdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTasksDialogOpen, setUserTasksDialogOpen] = useState(false);
  const [userTasks, setUserTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [mailSettings, setMailSettings] = useState({
    smtp_host: '', smtp_port: 587, smtp_encryption: 'starttls',
    imap_host: '', imap_port: 993, imap_encryption: 'ssl',
    smtp_user: '', smtp_password: '', sender_name: '', sender_email: '',
    mail_enabled: false,
    scenarios: { gorev_atandiginda: true, gorev_durumu_degistiginde: true, gorev_son_tarih_yaklastiginda: false, gorev_son_tarih_gun: 3 }
  });
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderData, setReminderData] = useState({ hatirlatma_gun: 0, hatirlatma_saat: '09:00', hatirlatma_sms: true, hatirlatma_email: true });
  const [smsSettings, setSmsSettings] = useState({
    api_key: '', secret_key: '', sms_sender: '',
    sms_enabled: false,
    scenarios: {
      gorev_atandiginda: true,
      gorev_durumu_degistiginde: true,
      gorev_son_tarih_yaklastiginda: false,
      gorev_son_tarih_gun: 3
    },
    mesaj_icerik: {
      baslik_goster: true,
      aciklama_goster: false,
      son_tarih_goster: true,
      oncelik_goster: false,
      atayan_goster: false
    }
  });
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    unvan: '',
        phone: '',
    phone: '',
    email: '',
    password: '',
    is_admin: false,
    is_admin_assistant: false,
    is_viewer: false,
    department: '',
    can_access_finance_module: false,
    can_view_grafikler: false,
    has_bakim_onarim_access: false,
    has_emlak_kamulastirma_access: false,
    has_isy_access: false,
    has_pk_access: false,
    has_finajan_access: false
  });

  useEffect(() => {
    if (!user?.is_admin && !user?.is_admin_assistant) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    if (user?.is_admin) { loadSmsSettings(); loadMailSettings(); }
  }, []); // eslint-disable-line

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, formData, {
        withCredentials: true
      });
      setCreateDialogOpen(false);
      setFormData({ 
        username: '', 
        name: '',
        unvan: '',
        phone: '',
        email: '', 
        password: '', 
        is_admin: false, 
        is_admin_assistant: false, 
        is_viewer: false, 
        department: '', 
        can_access_finance_module: false, 
        can_view_grafikler: false, 
        has_bakim_onarim_access: false, 
        has_emlak_kamulastirma_access: false,
        has_isy_access: false,
    has_pk_access: false,
    has_finajan_access: false
      });
      fetchUsers();
    } catch (error) {
      console.error('Create user error:', error);
      alert(error.response?.data?.detail || 'Kullanıcı oluşturulurken hata oluştu');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        username: formData.username,
        name: formData.name,
        unvan: formData.unvan,
        phone: formData.phone,
        email: formData.email,
        is_admin: formData.is_admin,
        is_admin_assistant: formData.is_admin_assistant,
        is_viewer: formData.is_viewer,
        department: formData.department,
        can_access_finance_module: formData.can_access_finance_module,
        can_view_grafikler: formData.can_view_grafikler,
        has_bakim_onarim_access: formData.has_bakim_onarim_access,
        has_emlak_kamulastirma_access: formData.has_emlak_kamulastirma_access,
        has_isy_access: formData.has_isy_access,
        has_pk_access: formData.has_pk_access,
        has_finajan_access: formData.has_finajan_access
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData, {
        withCredentials: true
      });
      setEditDialogOpen(false);
      setSelectedUser(null);
      setFormData({
         username: '',
        name: '',
        unvan: '',
        phone: '',
        email: '',
        password: '',
        is_admin: false,
        is_admin_assistant: false,
        is_viewer: false,
        department: '',
        can_access_finance_module: false,
        can_view_grafikler: false,
        has_bakim_onarim_access: false,
        has_emlak_kamulastirma_access: false,
        has_isy_access: false,
    has_pk_access: false,
    has_finajan_access: false
      });
      fetchUsers();
    } catch (error) {
      console.error('Update user error:', error);
      alert(error.response?.data?.detail || 'Kullanıcı güncellenirken hata oluştu');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        withCredentials: true
      });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Delete user error:', error);
      alert(error.response?.data?.detail || 'Kullanıcı silinirken hata oluştu');
    }
  };

  const openEditDialog = (userData) => {
    setSelectedUser(userData);
    setFormData({
      username: userData.username,
      name: userData.name,
      unvan: userData.unvan || '',
      phone: userData.phone || '',
      email: userData.email,
      password: '',
      is_admin: userData.is_admin,
      is_admin_assistant: userData.is_admin_assistant || false,
      is_viewer: userData.is_viewer || false,
      department: userData.department || '',
      can_access_finance_module: userData.can_access_finance_module || false,
      can_view_grafikler: userData.can_view_grafikler || false,
      has_bakim_onarim_access: userData.has_bakim_onarim_access || false,
      has_emlak_kamulastirma_access: userData.has_emlak_kamulastirma_access || false,
      has_isy_access: userData.has_isy_access || false,
      has_pk_access: userData.has_pk_access || false,
      has_finajan_access: userData.has_finajan_access || false
    });
    setEditDialogOpen(true);
  };

  const openUserTasksDialog = async (userData) => {
    setSelectedUser(userData);
    setUserTasksDialogOpen(true);
    setLoadingTasks(true);
    
    try {
      const response = await axios.get(`${API}/tasks`, {
        withCredentials: true
      });
      
      // Filter tasks for this specific user (including assigned_to_2 and assigned_to_3)
      const filteredTasks = response.data.filter(task => 
        task.assigned_to === userData.id || 
        task.assigned_to_2 === userData.id || 
        task.assigned_to_3 === userData.id
      );
      setUserTasks(filteredTasks);
    } catch (error) {
      console.error('Tasks fetch error:', error);
      setUserTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // PDF Export Functions
  const statusLabels = {
    not_started: 'Başlanmadı',
    planned: 'Planlanan',
    in_progress: 'Devam Ediyor',
    project_work: 'Proje İşleri',
    tender_phase: 'İhale Aşamasında',
    completed: 'Tamamlandı'
  };

  const priorityLabels = {
    low: 'Düşük',
    medium: 'Orta',
    high: 'Yüksek',
    urgent: 'Acil',
    very_urgent: 'Çok Acil'
  };

  // Türkçe karakterleri ASCII'ye çevir (PDF için)
  const toAscii = (text) => {
    if (!text) return '';
    const turkishMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U'
    };
    return text.split('').map(char => turkishMap[char] || char).join('');
  };

  const exportAllTasksAsPDF = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { withCredentials: true });
      const tasks = response.data;

      const groupedTasks = {};
      tasks.forEach(task => {
        if (!groupedTasks[task.status]) groupedTasks[task.status] = [];
        groupedTasks[task.status].push(task);
      });

      const groups = Object.keys(groupedTasks).map(status => ({
        title: statusLabels[status] || status,
        headers: ['Görev', 'Atanan', 'Öncelik', 'Son Tarih'],
        headerColor: '#374151',
        data: groupedTasks[status].map(task => [
          task.title,
          task.assigned_to_email || '-',
          priorityLabels[task.priority] || task.priority,
          new Date(task.due_date).toLocaleDateString('tr-TR')
        ])
      }));

      await exportGroupedTableToPDF(groups, 'tum-gorevler.pdf', 'Kurumsal İş Yönetimi - Tüm Görevler');
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  const exportAllData = async () => {
    try {
      const response = await axios.get(`${API}/export/all-data`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Excel dosyasını indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gsb-tum-veriler-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ Tüm veriler Excel olarak indirildi!');
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Excel oluşturulurken hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportUsersListAsPDF = async () => {
    try {
      const departmentLabels = {
        'bakim_onarim': 'Bakım-Onarım',
        'emlak_kamulastirma': 'Emlak ve Kamulaştırma',
        'yatirim_insaat': 'Yatırım ve İnşaat'
      };

      const unvanLabels = {
        'insaat_muhendisi': 'İnşaat Mühendisi',
        'makine_muhendisi': 'Makine Mühendisi',
        'elektrik_muhendisi': 'Elektrik/Elektronik Mühendisi',
        'orman_muhendisi': 'Orman Mühendisi',
        'mimar': 'Mimar',
        'buro_memuru': 'Büro Memuru',
        'grafiker': 'Grafiker',
        'tekniker': 'Tekniker',
        'teknisyen': 'Teknisyen',
        'mudur': 'Müdür',
        'sef': 'Şef',
        'vhki': 'Veri Hazırlama ve Kontrol İşletmeni',
        'kimya_muhendisi': 'Kimya Mühendisi',
        'ziraat_muhendisi': 'Ziraat Mühendisi',
        'spor_uzmani': 'Spor Uzmanı'
      };

      const groups = [];
      const tableHeaders = ['S.NO', 'Ad Soyad', 'Ünvan', 'Email', 'Alt Birim', 'Rol', 'Görev'];

      // Adminler
      const admins = users.filter(u => u.is_admin);
      if (admins.length > 0) {
        groups.push({
          title: 'ADMİNLER',
          headers: tableHeaders,
          headerColor: '#8b5cf6',
          data: admins.map((u, i) => [i + 1, u.name, unvanLabels[u.unvan] || u.unvan || '-', u.email, '-', 'Admin', (u.total_tasks || 0).toString()])
        });
      }

      // Alt birimler
      ['bakim_onarim', 'emlak_kamulastirma', 'yatirim_insaat'].forEach(dept => {
        const deptUsers = users.filter(u => u.department === dept && !u.is_admin);
        if (deptUsers.length > 0) {
          const deptAssistant = deptUsers.filter(u => u.is_admin_assistant);
          const deptEmployees = deptUsers.filter(u => !u.is_admin_assistant);
          const allDeptUsers = [...deptAssistant, ...deptEmployees];
          groups.push({
            title: (departmentLabels[dept] || dept).toUpperCase(),
            headers: tableHeaders,
            headerColor: dept === 'bakim_onarim' ? '#ef4444' : dept === 'emlak_kamulastirma' ? '#22c55e' : '#3b82f6',
            data: allDeptUsers.map((u, i) => [
              i + 1, u.name, unvanLabels[u.unvan] || u.unvan || '-', u.email,
              departmentLabels[dept], u.is_admin_assistant ? 'Admin Yardımcısı' : 'Çalışan',
              (u.total_tasks || 0).toString()
            ])
          });
        }
      });

      // Ünvan dağılımı özet tablosu
      const unvanCounts = {};
      users.forEach(u => {
        const unvan = u.unvan || 'Belirtilmemiş';
        unvanCounts[unvan] = (unvanCounts[unvan] || 0) + 1;
      });
      const unvanData = Object.entries(unvanCounts).map(([unvan, count]) => [
        unvanLabels[unvan] || unvan, count.toString()
      ]);
      unvanData.push(['TOPLAM ÇALIŞAN', users.length.toString()]);

      groups.push({
        title: 'ÜNVAN DAĞILIMI',
        headers: ['Ünvan', 'Adet'],
        headerColor: '#3b82f6',
        data: unvanData,
        hasTotalRow: true
      });

      await exportGroupedTableToPDF(groups, 'calisanlar-listesi.pdf', 'Kurumsal İş Yönetimi - Çalışanlar Listesi');
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  const exportUserTasksAsPDF = async (userId, userName) => {
    try {
      const response = await axios.get(`${API}/tasks`, { withCredentials: true });
      const allTasks = response.data;
      const userTasks = allTasks.filter(task => 
        task.assigned_to === userId || 
        task.assigned_to_2 === userId || 
        task.assigned_to_3 === userId
      );

      const headers = ['S.NO', 'Görev', 'Açıklama', 'Durum', 'Öncelik', 'Son Tarih', 'Son Durum'];
      const tasksData = userTasks.map((task, index) => {
        const lastStatus = task.status_updates && task.status_updates.length > 0 
          ? task.status_updates[task.status_updates.length - 1].update_text.substring(0, 50) + '...'
          : '-';
        return [
          index + 1,
          task.title,
          task.description.substring(0, 40) + '...',
          statusLabels[task.status] || task.status,
          priorityLabels[task.priority] || task.priority,
          new Date(task.due_date).toLocaleDateString('tr-TR'),
          lastStatus
        ];
      });

      await exportTableToPDF(headers, tasksData, `Kullanıcı Görevleri - ${userName}`, `${userName}-gorevler.pdf`, {
        subtitle: `Kullanıcı: ${userName}`
      });
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  // Password Management Functions
  const openChangePasswordDialog = (userData) => {
    setSelectedUser(userData);
    setNewPassword('');
    setChangePasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim()) {
      alert('Lütfen yeni şifre girin');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/change-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      
      alert('Şifre başarıyla değiştirildi');
      setChangePasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Change password error:', error);
      alert('Şifre değiştirilirken hata oluştu');
    }
  };

  // SMS Ayarları (durum rozetleri için; yönetim Admin Paneli > SMS Yönetimi sekmesine taşındı)
  const loadSmsSettings = async () => {
    try {
      const res = await axios.get(`${API}/sms-settings`, { withCredentials: true });
      setSmsSettings(res.data);
    } catch (error) {
      console.error('SMS ayarları yüklenemedi:', error);
    }
  };

  // Mail Ayarları (durum rozetleri için; yönetim Admin Paneli > Mail Yönetimi sekmesine taşındı)
  const loadMailSettings = async () => {
    try {
      const res = await axios.get(`${API}/mail-settings`, { withCredentials: true });
      setMailSettings(res.data);
    } catch (error) {
      console.error('Mail ayarları yüklenemedi:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Görevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/calendar')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Takvim
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-blue-500"
              >
                Kullanıcılar
              </button>
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">Admin</p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Kullanıcı Yönetimi</h2>
            <p className="text-gray-400">
              {user?.is_admin_assistant 
                ? 'Kullanıcıları görüntüleyin ve görevlerini kontrol edin' 
                : 'Kullanıcıları ekleyin, düzenleyin veya silin'}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap justify-end">
            <Button
              onClick={exportUsersListAsPDF}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Çalışanlar Listesini İndir
            </Button>
            {user?.is_admin && (
              <Button
                onClick={() => navigate('/site-admin')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="admin-panel-redirect-btn"
                title="Database yedek, SMS/Mail yönetimi, oturum ayarları ve denetim kaydı Admin Paneli'ne taşındı"
              >
                ⚙️ Admin Paneli
              </Button>
            )}

            {/* SMS Durum Bilgisi */}
            {user?.is_admin && smsSettings.sms_enabled && (
              <div className="flex items-center gap-2 bg-green-900/20 border border-green-800/50 rounded-lg px-3 py-1.5">
                <span className="text-green-400 text-xs font-medium">SMS Aktif:</span>
                <div className="flex gap-1.5">
                  {smsSettings.scenarios?.gorev_atandiginda && <span className="text-xs bg-green-800/50 text-green-300 px-2 py-0.5 rounded">Görev Atama</span>}
                  {smsSettings.scenarios?.gorev_durumu_degistiginde && <span className="text-xs bg-green-800/50 text-green-300 px-2 py-0.5 rounded">Durum Değişikliği</span>}
                  {smsSettings.scenarios?.gorev_son_tarih_yaklastiginda && <span className="text-xs bg-green-800/50 text-green-300 px-2 py-0.5 rounded">Son Tarih Hatırlatma</span>}
                </div>
              </div>
            )}
            {user?.is_admin && !smsSettings.sms_enabled && smsSettings.api_key && (
              <span className="text-xs text-yellow-500 bg-yellow-900/20 border border-yellow-800/50 rounded-lg px-3 py-1.5">SMS Pasif</span>
            )}
            {user?.is_admin && mailSettings.mail_enabled && (
              <div className="flex items-center gap-2 bg-indigo-900/20 border border-indigo-800/50 rounded-lg px-3 py-1.5">
                <span className="text-indigo-400 text-xs font-medium">Mail Aktif:</span>
                <div className="flex gap-1.5">
                  {mailSettings.scenarios?.gorev_atandiginda && <span className="text-xs bg-indigo-800/50 text-indigo-300 px-2 py-0.5 rounded">Görev Atama</span>}
                  {mailSettings.scenarios?.gorev_durumu_degistiginde && <span className="text-xs bg-indigo-800/50 text-indigo-300 px-2 py-0.5 rounded">Durum Değişikliği</span>}
                </div>
              </div>
            )}
            <Button
              onClick={exportAllTasksAsPDF}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Tüm Görevleri PDF Olarak İndir
            </Button>
            {!user?.is_admin_assistant && (
              <Button
                onClick={() => {
                  setFormData({
         username: '',
        name: '',
        unvan: '',
        phone: '',
        email: '',
        password: '',
        is_admin: false,
        is_admin_assistant: false,
        is_viewer: false,
        department: '',
        can_access_finance_module: false,
        can_view_grafikler: false,
        has_bakim_onarim_access: false,
        has_emlak_kamulastirma_access: false,
        has_isy_access: false,
    has_pk_access: false,
    has_finajan_access: false
      });
                  setCreateDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="create-user-button"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Kullanıcı Ekle
              </Button>
            )}
          </div>
        </div>

        {/* Users Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-grid">
          {users.map((u) => (
            <Card 
              key={u.id} 
              className="bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors cursor-pointer" 
              onClick={() => openUserTasksDialog(u)}
              data-testid={`user-card-${u.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full" />
                    <div>
                      <CardTitle className="text-white text-lg">{u.name}</CardTitle>
                      <p className="text-sm text-gray-400">{u.email}</p>
                      {u.phone && <p className="text-xs text-gray-500">{u.phone}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {u.is_founder && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                        👑 Founder
                      </Badge>
                    )}
                    {u.is_admin && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
                        Admin
                      </Badge>
                    )}
                    {u.is_admin_assistant && (
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">
                        Admin Yardımcısı
                      </Badge>
                    )}
                    {u.is_viewer && (
                      <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/50">
                        İzleyici
                      </Badge>
                    )}
                  </div>
                  {/* Modül Erişimleri */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {u.can_access_finance_module && <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[10px]">Finans</Badge>}
                    {u.has_bakim_onarim_access && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[10px]">Bakım</Badge>}
                    {u.has_emlak_kamulastirma_access && <Badge className="bg-teal-900/30 text-teal-400 border-teal-700/40 text-[10px]">Emlak</Badge>}
                    {u.has_isy_access && <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[10px]">İSY</Badge>}
                    {u.has_pk_access && <Badge className="bg-orange-900/30 text-orange-400 border-orange-700/40 text-[10px]">PK</Badge>}
                    {u.has_finajan_access && <Badge className="bg-violet-900/30 text-violet-400 border-violet-700/40 text-[10px]">FinAjan</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Task Statistics */}
                <div className="bg-gray-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Toplam Görev:</span>
                    <span className="text-white font-medium">{u.total_tasks || 0}</span>
                  </div>
                  {u.total_tasks > 0 && (
                    <>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Başlanmadı:</span>
                        <span className="text-gray-400">{u.not_started || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-cyan-500">Planlanan:</span>
                        <span className="text-cyan-400">{u.planned || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-blue-500">Devam Ediyor:</span>
                        <span className="text-blue-400">{u.in_progress || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-purple-500">Proje İşleri:</span>
                        <span className="text-purple-400">{u.project_work || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-orange-500">İhale Aşamasında:</span>
                        <span className="text-orange-400">{u.tender_phase || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-green-500">Tamamlandı:</span>
                        <span className="text-green-400">{u.completed || 0}</span>
                      </div>
                    </>
                  )}
                </div>

                {!user?.is_admin_assistant ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(u);
                        }}
                        variant="outline"
                        className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                        data-testid={`edit-user-${u.id}`}
                      >
                        Düzenle
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUser(u);
                          setDeleteDialogOpen(true);
                        }}
                        variant="outline"
                        className="flex-1 bg-red-900/20 border-red-700 text-red-400 hover:bg-red-900/40"
                        disabled={u.id === user?.id || u.is_founder}
                        data-testid={`delete-user-${u.id}`}
                      >
                        {u.is_founder ? '🔒 Korumalı' : 'Sil'}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          exportUserTasksAsPDF(u.id, u.name);
                        }}
                        variant="outline"
                        className="flex-1 bg-blue-900/20 border-blue-700 text-blue-400 hover:bg-blue-900/40"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        PDF İndir
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openChangePasswordDialog(u);
                        }}
                        variant="outline"
                        className="flex-1 bg-purple-900/20 border-purple-700 text-purple-400 hover:bg-purple-900/40"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Şifre Değiştir
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUser(u);
                          setReminderData({ hatirlatma_gun: u.hatirlatma_gun || 0, hatirlatma_saat: u.hatirlatma_saat || '09:00', hatirlatma_sms: u.hatirlatma_sms !== false, hatirlatma_email: u.hatirlatma_email !== false });
                          setReminderDialogOpen(true);
                        }}
                        variant="outline"
                        className="flex-1 bg-amber-900/20 border-amber-700 text-amber-400 hover:bg-amber-900/40"
                      >
                        Hatırlatma {u.hatirlatma_gun ? `(${u.hatirlatma_gun} gün, ${u.hatirlatma_saat || '09:00'})` : ''}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-xs text-blue-400">Görevleri görmek için karta tıklayın</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Users Task Chart */}
        {users.length > 0 && (
          <Card className="bg-gray-900 border-gray-800 mt-8">
            <CardHeader>
              <CardTitle className="text-white">Kullanıcı Görev Dağılımı</CardTitle>
              <CardDescription className="text-gray-400">
                Her kullanıcının toplam görev sayısı
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={users.map(u => ({
                    name: u.name.split(' ')[0], // Sadece isim
                    tasks: u.total_tasks || 0
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    stroke="#4b5563"
                  />
                  <YAxis
                    tick={{ fill: '#9ca3af' }}
                    stroke="#4b5563"
                    label={{ value: 'Görev Sayısı', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '6px',
                      color: '#fff'
                    }}
                    cursor={{ fill: '#374151' }}
                  />
                  <Bar dataKey="tasks" radius={[8, 8, 0, 0]}>
                    {users.map((u, index) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 360 / users.length}, 70%, 60%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
            <DialogDescription className="text-gray-400">
              Yeni kullanıcı bilgilerini girin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-username">Kullanıcı Adı *</Label>
              <Input
                id="create-username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="create-username-input"
                placeholder="Örn: aturna"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name">Ad Soyad *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="create-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-unvan">Ünvan</Label>
              <Select
                value={formData.unvan || "none"}
                onValueChange={(value) => setFormData({ ...formData, unvan: value === "none" ? "" : value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Ünvan seçin" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="none">Seçilmedi</SelectItem>
                  <SelectItem value="insaat_muhendisi">İnşaat Mühendisi</SelectItem>
                  <SelectItem value="makine_muhendisi">Makine Mühendisi</SelectItem>
                  <SelectItem value="elektrik_muhendisi">Elektrik/Elektronik Mühendisi</SelectItem>
                  <SelectItem value="orman_muhendisi">Orman Mühendisi</SelectItem>
                  <SelectItem value="mimar">Mimar</SelectItem>
                  <SelectItem value="buro_memuru">Büro Memuru</SelectItem>
                  <SelectItem value="grafiker">Grafiker</SelectItem>
                  <SelectItem value="tekniker">Tekniker</SelectItem>
                  <SelectItem value="teknisyen">Teknisyen</SelectItem>
                  <SelectItem value="mudur">Müdür</SelectItem>
                  <SelectItem value="sef">Şef</SelectItem>
                  <SelectItem value="vhki">Veri Hazırlama ve Kontrol İşletmeni</SelectItem>
                  <SelectItem value="kimya_muhendisi">Kimya Mühendisi</SelectItem>
                  <SelectItem value="ziraat_muhendisi">Ziraat Mühendisi</SelectItem>
                  <SelectItem value="spor_uzmani">Spor Uzmanı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-phone">Telefon</Label>
              <Input
                id="create-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="05XX XXX XX XX"
                data-testid="create-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="create-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Parola *</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="create-password-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-admin"
                checked={formData.is_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                data-testid="create-admin-switch"
              />
              <Label htmlFor="create-admin" className="cursor-pointer">Admin yetkisi ver</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-admin-assistant"
                checked={formData.is_admin_assistant}
                onCheckedChange={(checked) => setFormData({ ...formData, is_admin_assistant: checked })}
                data-testid="create-admin-assistant-switch"
              />
              <Label htmlFor="create-admin-assistant" className="cursor-pointer">Admin Yardımcısı (Alt Birim Yöneticisi)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-viewer"
                checked={formData.is_viewer}
                onCheckedChange={(checked) => setFormData({ ...formData, is_viewer: checked })}
                data-testid="create-viewer-switch"
              />
              <Label htmlFor="create-viewer" className="cursor-pointer">İzleyici (Sadece Görüntüleme Yetkisi)</Label>
            </div>
            {formData.is_admin_assistant && (
              <div className="space-y-2">
                <Label htmlFor="create-department" className="text-white">Alt Birim *</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData({ ...formData, department: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Alt birim seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="bakim_onarim">Bakım-Onarım</SelectItem>
                    <SelectItem value="emlak_kamulastirma">Emlak ve Kamulaştırma</SelectItem>
                    <SelectItem value="yatirim_insaat">Yatırım ve İnşaat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!formData.is_admin && !formData.is_admin_assistant && (
              <div className="space-y-2">
                <Label htmlFor="create-user-department" className="text-white">Alt Birim</Label>
                <Select
                  value={formData.department || "none"}
                  onValueChange={(value) => setFormData({ ...formData, department: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Alt birim seçin (opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Seçim Yapma</SelectItem>
                    <SelectItem value="bakim_onarim">Bakım-Onarım</SelectItem>
                    <SelectItem value="emlak_kamulastirma">Emlak ve Kamulaştırma</SelectItem>
                    <SelectItem value="yatirim_insaat">Yatırım ve İnşaat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Switch
                id="create-finance-access"
                checked={formData.can_access_finance_module}
                onCheckedChange={(checked) => setFormData({ ...formData, can_access_finance_module: checked })}
              />
              <Label htmlFor="create-finance-access" className="cursor-pointer">İhaleler/Ödenek/Hakediş Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-charts-access"
                checked={formData.can_view_grafikler}
                onCheckedChange={(checked) => setFormData({ ...formData, can_view_grafikler: checked })}
              />
              <Label htmlFor="create-charts-access" className="cursor-pointer">Grafikler Menüsü Görme</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-bakim-onarim-access"
                checked={formData.has_bakim_onarim_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_bakim_onarim_access: checked })}
              />
              <Label htmlFor="create-bakim-onarim-access" className="cursor-pointer">🔧 Bakım-Onarım Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-emlak-kamulastirma-access"
                checked={formData.has_emlak_kamulastirma_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_emlak_kamulastirma_access: checked })}
              />
              <Label htmlFor="create-emlak-kamulastirma-access" className="cursor-pointer">Emlak-Kamulaştırma Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-isy-access"
                checked={formData.has_isy_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_isy_access: checked })}
              />
              <Label htmlFor="create-isy-access" className="cursor-pointer">ISY - Yapım İhaleleri Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-pk-access"
                checked={formData.has_pk_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_pk_access: checked })}
              />
              <Label htmlFor="create-pk-access" className="cursor-pointer">PK - Periyodik Kontrol Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-finajan-access"
                checked={formData.has_finajan_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_finajan_access: checked })}
              />
              <Label htmlFor="create-finajan-access" className="cursor-pointer">FinAjan - AI Hedge Fund Modülü Erişimi</Label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              >
                İptal
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-user">
                Oluştur
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kullanıcıyı Düzenle</DialogTitle>
            <DialogDescription className="text-gray-400">
              Kullanıcı bilgilerini güncelleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-username">Kullanıcı Adı *</Label>
              <Input
                id="edit-username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="edit-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Ad Soyad *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-unvan">Ünvan</Label>
              <Select
                value={formData.unvan || "none"}
                onValueChange={(value) => setFormData({ ...formData, unvan: value === "none" ? "" : value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Ünvan seçin" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="none">Seçilmedi</SelectItem>
                  <SelectItem value="insaat_muhendisi">İnşaat Mühendisi</SelectItem>
                  <SelectItem value="makine_muhendisi">Makine Mühendisi</SelectItem>
                  <SelectItem value="elektrik_muhendisi">Elektrik/Elektronik Mühendisi</SelectItem>
                  <SelectItem value="orman_muhendisi">Orman Mühendisi</SelectItem>
                  <SelectItem value="mimar">Mimar</SelectItem>
                  <SelectItem value="buro_memuru">Büro Memuru</SelectItem>
                  <SelectItem value="grafiker">Grafiker</SelectItem>
                  <SelectItem value="tekniker">Tekniker</SelectItem>
                  <SelectItem value="teknisyen">Teknisyen</SelectItem>
                  <SelectItem value="mudur">Müdür</SelectItem>
                  <SelectItem value="sef">Şef</SelectItem>
                  <SelectItem value="vhki">Veri Hazırlama ve Kontrol İşletmeni</SelectItem>
                  <SelectItem value="kimya_muhendisi">Kimya Mühendisi</SelectItem>
                  <SelectItem value="ziraat_muhendisi">Ziraat Mühendisi</SelectItem>
                  <SelectItem value="spor_uzmani">Spor Uzmanı</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefon</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="05XX XXX XX XX"
                data-testid="edit-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                required
                data-testid="edit-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">Yeni Parola (opsiyonel)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Değiştirmek istemiyorsanız boş bırakın"
                data-testid="edit-password-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-admin"
                checked={formData.is_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                disabled={selectedUser?.is_founder}
                data-testid="edit-admin-switch"
              />
              <Label htmlFor="edit-admin" className="cursor-pointer">
                Admin yetkisi {selectedUser?.is_founder && <span className="text-yellow-400 ml-2">(🔒 Founder)</span>}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-admin-assistant"
                checked={formData.is_admin_assistant}
                onCheckedChange={(checked) => setFormData({ ...formData, is_admin_assistant: checked })}
                data-testid="edit-admin-assistant-switch"
              />
              <Label htmlFor="edit-admin-assistant" className="cursor-pointer">Admin Yardımcısı (Alt Birim Yöneticisi)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-viewer"
                checked={formData.is_viewer}
                onCheckedChange={(checked) => setFormData({ ...formData, is_viewer: checked })}
                data-testid="edit-viewer-switch"
              />
              <Label htmlFor="edit-viewer" className="cursor-pointer">İzleyici (Sadece Görüntüleme Yetkisi)</Label>
            </div>
            {formData.is_admin_assistant && (
              <div className="space-y-2">
                <Label htmlFor="edit-department" className="text-white">Alt Birim *</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData({ ...formData, department: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Alt birim seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="bakim_onarim">Bakım-Onarım</SelectItem>
                    <SelectItem value="emlak_kamulastirma">Emlak ve Kamulaştırma</SelectItem>
                    <SelectItem value="yatirim_insaat">Yatırım ve İnşaat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!formData.is_admin && !formData.is_admin_assistant && (
              <div className="space-y-2">
                <Label htmlFor="edit-user-department" className="text-white">Alt Birim</Label>
                <Select
                  value={formData.department || "none"}
                  onValueChange={(value) => setFormData({ ...formData, department: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Alt birim seçin (opsiyonel)" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Seçim Yapma</SelectItem>
                    <SelectItem value="bakim_onarim">Bakım-Onarım</SelectItem>
                    <SelectItem value="emlak_kamulastirma">Emlak ve Kamulaştırma</SelectItem>
                    <SelectItem value="yatirim_insaat">Yatırım ve İnşaat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-finance-access"
                checked={formData.can_access_finance_module}
                onCheckedChange={(checked) => setFormData({ ...formData, can_access_finance_module: checked })}
              />
              <Label htmlFor="edit-finance-access" className="cursor-pointer">İhaleler/Ödenek/Hakediş Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-charts-access"
                checked={formData.can_view_grafikler}
                onCheckedChange={(checked) => setFormData({ ...formData, can_view_grafikler: checked })}
              />
              <Label htmlFor="edit-charts-access" className="cursor-pointer">Grafikler Menüsü Görme</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-bakim-onarim-access"
                checked={formData.has_bakim_onarim_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_bakim_onarim_access: checked })}
              />
              <Label htmlFor="edit-bakim-onarim-access" className="cursor-pointer">🔧 Bakım-Onarım Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-emlak-kamulastirma-access"
                checked={formData.has_emlak_kamulastirma_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_emlak_kamulastirma_access: checked })}
              />
              <Label htmlFor="edit-emlak-kamulastirma-access" className="cursor-pointer">Emlak-Kamulaştırma Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-isy-access"
                checked={formData.has_isy_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_isy_access: checked })}
              />
              <Label htmlFor="edit-isy-access" className="cursor-pointer">ISY - Yapım İhaleleri Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-pk-access"
                checked={formData.has_pk_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_pk_access: checked })}
              />
              <Label htmlFor="edit-pk-access" className="cursor-pointer">PK - Periyodik Kontrol Modülü Erişimi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-finajan-access"
                checked={formData.has_finajan_access}
                onCheckedChange={(checked) => setFormData({ ...formData, has_finajan_access: checked })}
              />
              <Label htmlFor="edit-finajan-access" className="cursor-pointer">FinAjan - AI Hedge Fund Modülü Erişimi</Label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              >
                İptal
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-edit-user">
                Güncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              <strong>{selectedUser?.name}</strong> kullanıcısını silmek istediğinizden emin misiniz? 
              Bu işlem geri alınamaz ve kullanıcının tüm görevleri silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-user"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Tasks Dialog */}
      <Dialog open={userTasksDialogOpen} onOpenChange={setUserTasksDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedUser?.name} - Görevler</DialogTitle>
            <DialogDescription className="text-gray-400">
              Bu kullanıcıya atanan tüm görevler
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {loadingTasks ? (
              <div className="text-center py-12">
                <div className="text-gray-400">Görevler yükleniyor...</div>
              </div>
            ) : userTasks.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {userTasks.map((task) => (
                  <Card key={task.id} className="bg-gray-800 border-gray-700">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg text-white">{task.title}</CardTitle>
                        <div className="flex gap-2">
                          {task.priority === 'low' && (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Düşük</Badge>
                          )}
                          {task.priority === 'medium' && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Orta</Badge>
                          )}
                          {task.priority === 'high' && (
                            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">Yüksek</Badge>
                          )}
                          {task.priority === 'urgent' && (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Acil</Badge>
                          )}
                          {task.priority === 'very_urgent' && (
                            <Badge className="bg-pink-500/20 text-pink-400 border-pink-500/50">Çok Acil</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-400 mt-2">{task.description}</p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-4 text-sm">
                        {task.status === 'not_started' && (
                          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/50">Başlanmadı</Badge>
                        )}
                        {task.status === 'planned' && (
                          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/50">Planlanan</Badge>
                        )}
                        {task.status === 'in_progress' && (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Devam Ediyor</Badge>
                        )}
                        {task.status === 'project_work' && (
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">Proje İşleri</Badge>
                        )}
                        {task.status === 'tender_phase' && (
                          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">İhale Aşamasında</Badge>
                        )}
                        {task.status === 'completed' && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Tamamlandı</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>Başlangıç: {new Date(task.start_date).toLocaleDateString('tr-TR')}</p>
                        <p>Son Tarih: {new Date(task.due_date).toLocaleDateString('tr-TR')}</p>
                        <p>Termin: {new Date(task.deadline_date).toLocaleDateString('tr-TR')}</p>
                      </div>
                      {task.status_updates && task.status_updates.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <p className="text-xs font-medium text-gray-400 mb-2">Son Durum Güncellemeleri ({task.status_updates.length})</p>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {task.status_updates.slice(-3).reverse().map((update, index) => (
                              <div key={index} className="bg-gray-900 rounded p-2 text-xs">
                                <div className="flex justify-between items-start mb-1">
                                  <span className="font-medium text-blue-400">{update.updated_by_name}</span>
                                  <span className="text-gray-500">{new Date(update.created_at).toLocaleDateString('tr-TR')}</span>
                                </div>
                                <p className="text-gray-300">{update.update_text}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400">Bu kullanıcıya henüz görev atanmamış</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-6">
            <Button
              onClick={() => setUserTasksDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Şifre Değiştir</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.name} için yeni şifre belirleyin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-white">Yeni Şifre</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Yeni şifre girin"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setChangePasswordDialogOpen(false)}
              variant="ghost"
              className="text-gray-400"
            >
              İptal
            </Button>
            <Button
              onClick={handleChangePassword}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Şifreyi Değiştir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatma Ayarı Diyalogu */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">Hatırlatma Ayarı - {selectedUser?.name}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Bu kullanıcıya görev hatırlatması kaç günde bir gönderilsin?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-white">Hatırlatma Sıklığı (Gün)</Label>
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 5, 7, 10, 15, 30].map(g => (
                  <Button key={g} size="sm" onClick={() => setReminderData({...reminderData, hatirlatma_gun: g})}
                    className={`h-8 px-3 text-xs ${reminderData.hatirlatma_gun === g ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                  >{g === 0 ? 'Kapalı' : `${g} Gün`}</Button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {reminderData.hatirlatma_gun === 0 ? 'Hatırlatma kapalı' : `Her ${reminderData.hatirlatma_gun} günde bir hatırlatma gönderilecek`}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Gönderim Saati</Label>
              <div className="flex flex-wrap gap-2">
                {['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'].map(s => (
                  <Button key={s} size="sm" onClick={() => setReminderData({...reminderData, hatirlatma_saat: s})}
                    className={`h-8 px-3 text-xs ${reminderData.hatirlatma_saat === s ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                  >{s}</Button>
                ))}
              </div>
            </div>

            <div className="space-y-3 p-3 bg-gray-800/50 rounded-lg">
              <p className="text-sm text-white font-medium">Gönderim Kanalları</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">SMS ile hatırlat</span>
                <Button size="sm" onClick={() => setReminderData({...reminderData, hatirlatma_sms: !reminderData.hatirlatma_sms})}
                  className={`h-7 px-3 text-xs ${reminderData.hatirlatma_sms ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                  {reminderData.hatirlatma_sms ? 'Açık' : 'Kapalı'}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">E-posta ile hatırlat</span>
                <Button size="sm" onClick={() => setReminderData({...reminderData, hatirlatma_email: !reminderData.hatirlatma_email})}
                  className={`h-7 px-3 text-xs ${reminderData.hatirlatma_email ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                  {reminderData.hatirlatma_email ? 'Açık' : 'Kapalı'}
                </Button>
              </div>
            </div>
          </div>

          {/* Şimdi Gönder */}
          <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg space-y-2">
            <p className="text-sm text-white font-medium">Şimdi Hatırlatma Gönder</p>
            <p className="text-xs text-gray-400">Tekrarlama sıklığından bağımsız olarak bu kullanıcıya hemen görev hatırlatması gönderir</p>
            <Button size="sm" onClick={async () => {
              try {
                const res = await axios.post(`${API}/send-reminder-now/${selectedUser.id}`, {}, { withCredentials: true });
                alert(res.data.message || 'Hatırlatma gönderildi!');
              } catch (e) {
                alert('Hata: ' + (e.response?.data?.detail || e.message));
              }
            }} className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-4 text-xs">
              Şimdi Gönder
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderDialogOpen(false)} className="bg-gray-800 border-gray-700 text-white">İptal</Button>
            <Button onClick={async () => {
              try {
                await axios.put(`${API}/users/${selectedUser.id}/reminder`, reminderData, { withCredentials: true });
                alert('Hatırlatma ayarı kaydedildi');
                setReminderDialogOpen(false);
                fetchUsers();
              } catch (error) {
                alert('Hata: ' + (error.response?.data?.detail || error.message));
              }
            }} className="bg-blue-600 hover:bg-blue-700 text-white">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
